import type { ReportDataResponse } from '@dashhudson/api-client/dashboard/model/raw';

import mapValues from 'lodash/mapValues';

import { ALL_CHANNELS_KEY } from '@/models/dashboards/channels.enum';

interface StatsTree {
  [key: string]: StatsTree;
}

interface GraphSeriesArgs {
  brandId: number | string;
  channel: string;
  metric: string;
}

// Renames `fromKey` to `toKey` on the target object in place. No-op when the
// target is missing or the key is absent. Used to alias metric keys across
// graph stats responses during the v1 → v2 engine rollout.
export function renameMetricKey(
  target: Record<string, unknown> | null | undefined,
  fromKey: string,
  toKey: string,
): void {
  if (!target || !(fromKey in target)) return;
  target[toKey] = target[fromKey];
  delete target[fromKey];
}

export function selectGraphSeries(
  response: ReportDataResponse | null | undefined,
  { brandId, channel, metric }: GraphSeriesArgs,
): StatsTree | undefined {
  const root = response?.data as StatsTree | undefined;
  // Shape A — channel-keyed top level (target shape). Use `!== undefined` so
  // an empty {} response is treated as "Shape A present, no data" and does
  // not silently fall through to Shape B.
  const shapeA = root?.[channel]?.metrics?.[metric]?.[channel];
  if (shapeA !== undefined) return shapeA;
  // Single Channel - keyed metrics at the top level, each series nested under ALL_CHANNELS
  const v2Series = root?.metrics?.[metric]?.[ALL_CHANNELS_KEY];
  if (v2Series !== undefined) return v2Series;
  // TODO(sc-195103-cleanup): remove Shape B fallback once backend migration completes.
  return root?.[brandId]?.[channel]?.[metric];
}

export function selectChannelMetricsBag(
  response: ReportDataResponse | null | undefined,
  { brandId, channel }: { brandId: number | string; channel: string },
): Record<string, StatsTree> | StatsTree | undefined {
  const root = response?.data as StatsTree | undefined;
  // Shape A — channel-keyed top level (target shape)
  const shapeAMetrics = root?.[channel]?.metrics;
  if (shapeAMetrics) {
    return mapValues(shapeAMetrics, (perChannel) => perChannel?.[channel]);
  }
  // Single Channel - keyed metrics at the top level, each series nested under ALL_CHANNELS
  if (root?.metrics) {
    return mapValues(root.metrics, (perChannel) => perChannel?.[ALL_CHANNELS_KEY]);
  }
  // TODO(sc-195103-cleanup): remove Shape B fallback once backend migration completes.
  return root?.[brandId]?.[channel];
}

// Returns { <CHANNEL>: { <date>: value } } for a metric across all channels in
// the response, regardless of shape. Used by consumers that need to enumerate
// channels themselves (multi-channel charts).
export function selectChannelsForMetric(
  response: ReportDataResponse | null | undefined,
  { brandId, metric }: { brandId: number | string; metric: string },
): Record<string, StatsTree> | undefined {
  const root = response?.data as StatsTree | undefined;
  if (!root) return undefined;

  // Shape A — channel-keyed top level (target shape)
  const shapeA: Record<string, StatsTree> = {};
  Object.entries(root).forEach(([key, bucket]) => {
    if (key === String(brandId)) return;
    const series = bucket?.metrics?.[metric]?.[key];
    if (series !== undefined) shapeA[key] = series;
  });
  if (Object.keys(shapeA).length > 0) return shapeA;

  // TODO(sc-195103-cleanup): remove Shape B fallback once backend migration completes.
  return root[brandId]?.metrics?.[metric];
}
